import {
  DetailedHTMLProps,
  Dispatch,
  ImgHTMLAttributes,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from "react";
import { FileX03 } from "untitledui-js-base";

import { Document, Thumbnail, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { createPortal } from "react-dom";
import { PrintAllContext } from "views/applications-id-report/providers/PrintProvider";
import { Section } from "../Section";
import { cx } from "shared/utils";

export const Fallback = () => (
  <div className="pointer-events-none flex h-full max-w-[142px] gap-4 rounded-lg border border-alpha-40 bg-background-900 py-4 shadow-sm">
    <div className="mt-14 mx-auto">
      <FileX03 className="mx-auto mb-2 h-6 w-6 stroke-content-700" />
      <p className="whitespace-nowrap text-center text-xs font-medium text-content-400 hover:no-underline	">
        Unable to display file
      </p>
    </div>
  </div>
);

export const DocumentError = ({
  setError
}: {
  setError: Dispatch<SetStateAction<boolean>>;
}) => {
  useEffect(() => {
    setError(true);
  }, []);

  return null;
};

export const CreateDocumentPortal = ({
  url,
  setError,
  docName
}: {
  url: string;
  setError: Dispatch<SetStateAction<boolean>>;
  docName?: string;
}) => {
  const [rootCreated, setRootCreated] = useState(false);
  const { documentPrintingState } = useContext(PrintAllContext);
  const { setDocuments } = useContext(PrintAllContext);
  const [numPages, setNumPages] = useState(0);

  useEffect(() => {
    if (
      documentPrintingState?.every((d) => d.status === "ready") &&
      !rootCreated
    ) {
      const root = document.createElement("div");
      root.id = url;
      document.getElementById("printable_documents_root")?.appendChild(root);
      setRootCreated(true);
    }
  }, [documentPrintingState, url]);

  return (
    <div className="hidden print:block">
      <p>Assets attached at the bottom of the application</p>
      {rootCreated &&
        createPortal(
          <Section sectionTitle={docName}>
            <Document
              key={`printable_${url}_${numPages}`}
              file={url}
              error={<DocumentError setError={setError} />}
              onLoadSuccess={({ numPages: num }) => {
                setNumPages(num);
                const loadingDocuments = Array(num)
                  .fill(false)
                  .reduce((acc, curr, idx) => {
                    return { ...acc, [`page_${idx}_${url}`]: curr };
                  }, {});

                setDocuments((prev: any) => {
                  return {
                    ...loadingDocuments,
                    ...(prev || {})
                  };
                });
              }}
            >
              {Array(numPages)
                .fill(null)
                .map((_, index) => {
                  return (
                    <Page
                      renderTextLayer={false}
                      key={`page_${index}_${url}`}
                      pageNumber={index + 1}
                      className="px-10 hidden print:block"
                      onRenderSuccess={() => {
                        setDocuments((prev) => ({
                          ...prev,
                          [`page_${index}_${url}`]: true
                        }));
                      }}
                    />
                  );
                })}
            </Document>
          </Section>,
          document.getElementById("printable_documents_root")!
        )}
    </div>
  );
};

export const ImageWithFallback = ({
  src,
  ...props
}: DetailedHTMLProps<
  ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>) => {
  const [error, setError] = useState(false);

  if (error) return <Fallback />;

  return (
    <img src={src} onError={() => setError(true)} alt={props.alt} {...props} />
  );
};

export const AssetItem = ({
  url,
  ignorePrint,
  content_type,
  // filename,
  assetName
}: {
  url: string;
  content_type: string;
  filename?: string;
  assetName?: string;
  ignorePrint?: boolean;
}) => {
  const [error, setError] = useState(false);

  return (
    <a
      href={url}
      target="_blank"
      className={cx(
        "block min-w-[142px] shrink-0 rounded-lg text-primary-500 hover:no-underline",
        content_type === "application/pdf" && !error ? "h-auto" : "h-[195px]"
      )}
      download
      rel="noreferrer"
    >
      {content_type.includes("image/") ? (
        <ImageWithFallback
          src={url}
          alt="Error displaying submitted image"
          className="h-[195px] w-auto rounded-lg shadow-sm"
        />
      ) : content_type === "application/pdf" && !error ? (
        <div className="h-full">
          <Document
            key={`pdf_${url}_1`}
            file={url}
            error={<DocumentError setError={setError} />}
            className="bg-white pointer-events-none no-print h-full max-w-[142px] rounded-lg border border-alpha-40 shadow-xs"
          >
            <div className="px-4 pt-4">
              <Thumbnail height={128} pageNumber={1} />
            </div>

            <p className="px-6 py-3 truncate border-t border-alpha-40 text-center text-xs font-medium text-content-1000 hover:no-underline">
              {assetName || `File:${content_type}`}
            </p>
          </Document>

          {!ignorePrint ? (
            <CreateDocumentPortal
              url={url}
              setError={setError}
              docName={assetName || `File:${content_type}`}
            />
          ) : null}
        </div>
      ) : content_type === "video/webm" ? (
        <video
          autoPlay
          controls
          loop
          muted
          className="h-[195px] w-auto rounded-lg shadow-sm"
          src={url}
        >
          <a href={url}>Download</a>
        </video>
      ) : (
        <Fallback />
      )}
    </a>
  );
};
